import dayjs from 'dayjs';
import {
  WeeklyScheduleInput,
  SeniorCareRecipientRelationshipType,
  ServiceType,
} from '@/__generated__/globalTypes';
import { SxProps, Theme } from '@mui/material';

export type EnrollmentServiceType =
  | ServiceType.CHILD_CARE
  | ServiceType.HOUSEKEEPING
  | ServiceType.PET_CARE
  | ServiceType.SENIOR_CARE
  | ServiceType.TUTORING;

export type JobTypes = 'recurring' | 'onetime' | 'livein';

export type JobHours = {
  min: string;
  max: string;
};

export interface StateCheckingResponse {
  redirectCalled: boolean;
  readableMessage?: string;
}

type DateTime = {
  date: string | null;
  time: string;
};

export type DateTimeBlock = {
  start: DateTime;
  end: DateTime;
};

export type OneTimeCare = {
  schedule: DateTimeBlock;
};

export type Rate = {
  minimum: number;
  maximum: number;
  legalMinimum: number;
  rateRetrieved?: boolean;
  average?: number;
};
export type Member = {
  displayName: string;
  imageURL: string;
};
export type Caregiver = {
  member: Member;
  avgReviewRating: number;
  yearsOfExperience: number;
};

export type DayOfWeek = keyof WeeklyScheduleInput;
export type WeeklySchedule = WeeklyScheduleInput;

export interface PartsOfDay {
  morning?: boolean;
  afternoon?: boolean;
  evening?: boolean;
  overnight?: boolean;
}

// will probably rename this to Availability once implementation gets carried over to post a job
export type Recurring = {
  careTimes: PartsOfDay;
  schedule: WeeklySchedule;
  scheduleMayVary: boolean;
  specificTimes: boolean;
  timesAppliedToAllDays: boolean;
  start: string;
  end: string | null;
};

export type Age =
  | 'THIRTIES'
  | 'FORTIES'
  | 'FIFTIES'
  | 'SIXTIES'
  | 'SEVENTIES'
  | 'EIGHTIES'
  | 'NINETIES'
  | 'HUNDREDS';

const intentions = ['NOW', 'WEEK', 'MONTH', 'UNDECIDED'] as const;
export type Intent = (typeof intentions)[number];

export type Gender = 'FEMALE' | 'MALE';

export type LovedOne = {
  whoNeedsCare: SeniorCareRecipientRelationshipType | null;
  age: Age;
  gender: Gender;
  details: string | undefined;
};

export type IdealCaregiver = {
  gender: Gender | null;
  details: string | undefined;
  submissionAttempted: boolean;
  attempts: number;
};

// Federal minimum wage default rounded up
export const DEFAULT_LEGAL_MIN = 8;
export const DEFAULT_MIN = 14;
export const DEFAULT_AVG_MIN = 14;
export const DEFAULT_AVG_MAX = 22;
export const DEFAULT_MAX_ALLOWED = 50;
export const DEFAULT_AVG = (DEFAULT_AVG_MIN + DEFAULT_AVG_MAX) / 2;
export const DEFAULT_WAGE_STEP = 10;
export const AVG_TO_MAX_FACTOR = 1.2;
export const DEFAULT_WAGE_RANGE = [DEFAULT_MIN, DEFAULT_MIN + DEFAULT_WAGE_STEP];

export const DEFAULT_GENDER = 'FEMALE';
export const DEFAULT_WHO_NEEDS_CARE = 'PARENT' as SeniorCareRecipientRelationshipType;
export const DEFAULT_AGE = 'EIGHTIES';

export const initialRecurringState = {
  recurring: {
    careTimes: {},
    schedule: {},
    scheduleMayVary: false,
    specificTimes: false,
    timesAppliedToAllDays: false,
    start: dayjs().format('YYYY-MM-DD'),
    end: null,
  },
};

export interface MemberPhoto {
  thumbnailUrl: string;
  url: string;
}

export interface Location {
  zipcode: string;
  city: string;
  state: string;
  lat?: number | null;
  lng?: number | null;
}

export enum CCCaregiverAttributes {
  COLLEGE_DEGREE = 'COLLEGE_DEGREE',
  COLLEGE_EDUCATED = 'COLLEGE_EDUCATED',
  OWN_TRANSPORTATION = 'OWN_TRANSPORTATION',
  DOES_NOT_SMOKE = 'DOES_NOT_SMOKE',
  CPR_TRAINED = 'CPR_TRAINED',
  FIRST_AID_OR_CPR = 'FIRST_AID_OR_CPR',
  COMFORTABLE_WITH_PETS = 'COMFORTABLE_WITH_PETS',
  SPECIAL_NEEDS_EXPERIENCE = 'SPECIAL_NEEDS_EXPERIENCE',
  MULTILINGUAL = 'MULTILINGUAL',
}

export type JobPostData = {
  cargiverAttributes: Array<string>;
};

export const initialJobState = {
  cargiverAttributes: [],
};

export type OptionalReducerData = {
  type: string;
  prefix: string;
};

export enum FlowType {
  SEEKER_CHILD_CARE = 'SEEKER_CHILD_CARE',
  PROVIDER_MV_ACCOUNT_CREATION = 'PROVIDER_MV_ACCOUNT_CREATION',
  PROVIDER_MV_DETAILS = 'PROVIDER_MV_DETAILS',
}

export type SxClassProps = Record<string, SxProps<Theme>>;
